
import React from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useSettings } from "@/context/SettingsContext";
import { useAuth } from "@/context/SafeAuthProvider";
import { getRpgThemeClasses } from "@/lib/utils";
import { Scroll, BookOpen, Shield, Sparkles } from "lucide-react";

interface HistoryEmptyStateProps {
  onCreateNew: () => void;
}

const HistoryEmptyState: React.FC<HistoryEmptyStateProps> = ({ onCreateNew }) => {
  const { darkMode, enableRpgMode } = useSettings();
  const { user } = useAuth();
  const rpgTheme = getRpgThemeClasses(enableRpgMode, darkMode);

  return (
    <div className={cn(
      "text-center py-12 relative",
      enableRpgMode ? rpgTheme.text : (darkMode ? "text-[#FBF3F1]/70" : "text-[#1E0C46]/70")
    )}>
      {user ? (
        <div className="space-y-4">
          {enableRpgMode ? (
            <div className="relative">
              <Shield className="mx-auto h-16 w-16 text-amber-500/60 mb-4" />
              <Sparkles className="absolute top-2 right-1/2 transform translate-x-8 h-6 w-6 text-amber-400/40" />
            </div>
          ) : (
            <BookOpen className="mx-auto h-16 w-16 text-current opacity-60 mb-4" />
          )}
          
          <div className="space-y-2">
            <p className={cn(
              "text-lg font-medium",
              enableRpgMode && "drop-shadow-sm"
            )}>
              {enableRpgMode 
                ? "Your Chronicle Awaits" 
                : "No Journal Entries Yet"
              }
            </p>
            <p className={cn(
              "mb-4 max-w-md mx-auto",
              enableRpgMode && "drop-shadow-sm"
            )}>
              {enableRpgMode 
                ? "The pages of your adventure chronicle remain unwritten. Begin your first quest to start documenting your legendary journey through the realm of self-discovery." 
                : "You haven't created any journal entries yet. Start your journey of self-reflection and problem-solving."
              }
            </p>
          </div>
          
          <Button 
            onClick={onCreateNew}
            className={cn(
              "text-base",
              enableRpgMode ? rpgTheme.button : ""
            )}
          >
            {enableRpgMode ? (
              <>
                <Scroll className="mr-2 h-5 w-5" />
                Begin Your First Quest
              </>
            ) : (
              "Create Your First Entry"
            )}
          </Button>
        </div>
      ) : (
        <p>Please sign in to view your journal history.</p>
      )}
    </div>
  );
};

export default HistoryEmptyState;
